import { z } from "zod"

// Basic enums derived from the XML
export const QtiCardinalitySchema = z.enum(["single", "multiple", "ordered"]) // seen: single, multiple, ordered
export const QtiBaseTypeSchema = z.enum(["identifier", "string", "float", "integer", "boolean", "directedPair", "pair"]) // seen: identifier, float, directedPair

// SimpleChoice: <qti-simple-choice identifier="A"> ... inner XHTML/MathML ... </qti-simple-choice>
export const SimpleChoiceSchema = z.object({
	identifier: z.string().min(1),
	contentHtml: z.string(), // Allows "" for empty or image-only content
	inlineFeedbackHtml: z.string().optional() // Optional per-choice feedback (qti-feedback-inline)
})
export type SimpleChoice = z.infer<typeof SimpleChoiceSchema>

// InlineChoice: <qti-inline-choice identifier="ID"> ... inner XHTML/MathML ... </qti-inline-choice>
export const InlineChoiceSchema = z.object({
	identifier: z.string().min(1),
	contentHtml: z.string()
})
export type InlineChoice = z.infer<typeof InlineChoiceSchema>

// ChoiceInteraction: <qti-choice-interaction response-identifier="RESPONSE" shuffle="true" min-choices="1" max-choices="1"> ...
export const ChoiceInteractionCoreSchema = z.object({
	responseIdentifier: z.string().min(1),
	shuffle: z.boolean(),
	minChoices: z.number().int().min(0),
	maxChoices: z.number().int().min(0), // QTI 3.0: 0 = no restriction (unlimited selections)
	promptHtml: z.string(),
	choices: z.array(SimpleChoiceSchema).min(1)
})

// Extensible discriminated union for interactions
export const ChoiceInteractionSchema = ChoiceInteractionCoreSchema.extend({ type: z.literal("choiceInteraction") })
export type ChoiceInteraction = z.infer<typeof ChoiceInteractionSchema>

// InlineChoiceInteraction: <qti-inline-choice-interaction response-identifier="RESPONSE" shuffle="true"> ...
export const InlineChoiceInteractionCoreSchema = z.object({
	responseIdentifier: z.string().min(1),
	shuffle: z.boolean(),
	choices: z.array(InlineChoiceSchema).min(1)
})
export const InlineChoiceInteractionSchema = InlineChoiceInteractionCoreSchema.extend({
	type: z.literal("inlineChoiceInteraction")
})
export type InlineChoiceInteraction = z.infer<typeof InlineChoiceInteractionSchema>

// TextEntryInteraction: <qti-text-entry-interaction response-identifier="RESPONSE" expected-length="15" ...> ...
export const TextEntryInteractionCoreSchema = z.object({
	responseIdentifier: z.string().min(1),
	expectedLength: z.number().int().min(0).optional(),
	placeholderText: z.string().optional(),
	patternMask: z.string().optional()
})
export const TextEntryInteractionSchema = TextEntryInteractionCoreSchema.extend({
	type: z.literal("textEntryInteraction")
})
export type TextEntryInteraction = z.infer<typeof TextEntryInteractionSchema>

// OrderInteraction: <qti-order-interaction response-identifier="RESPONSE" shuffle="true" orientation="vertical"> ...
export const OrderInteractionCoreSchema = z.object({
	responseIdentifier: z.string().min(1),
	shuffle: z.boolean(),
	minChoices: z.number().int().min(0),
	maxChoices: z.number().int().min(0).optional(), // 0 or undefined usually means "all"
	orientation: z.enum(["vertical", "horizontal"]),
	promptHtml: z.string(),
	choices: z.array(SimpleChoiceSchema).min(1)
})
export const OrderInteractionSchema = OrderInteractionCoreSchema.extend({
	type: z.literal("orderInteraction")
})
export type OrderInteraction = z.infer<typeof OrderInteractionSchema>

// GapText: <qti-gap-text identifier="A" match-max="1"> ... content ... </qti-gap-text>
export const GapTextSchema = z.object({
	identifier: z.string().min(1),
	contentHtml: z.string(),
	matchMax: z.number().int().min(0) // 0 = unlimited
})
export type GapText = z.infer<typeof GapTextSchema>

// Gap: <qti-gap identifier="G1" /> - the drop target placeholder
export const GapSchema = z.object({
	identifier: z.string().min(1)
})
export type Gap = z.infer<typeof GapSchema>

// GapMatchInteraction: <qti-gap-match-interaction response-identifier="RESPONSE" shuffle="true"> ...
export const GapMatchInteractionCoreSchema = z.object({
	responseIdentifier: z.string().min(1),
	shuffle: z.boolean(),
	gapTexts: z.array(GapTextSchema).min(1), // Draggable source tokens
	gaps: z.array(GapSchema).min(1), // Drop target placeholders
	contentHtml: z.string() // HTML content with gap placeholders
})
export const GapMatchInteractionSchema = GapMatchInteractionCoreSchema.extend({
	type: z.literal("gapMatchInteraction")
})
export type GapMatchInteraction = z.infer<typeof GapMatchInteractionSchema>

// AssociableChoice: <qti-simple-associable-choice identifier="A" match-max="1"> ... </qti-simple-associable-choice>
export const AssociableChoiceSchema = z.object({
	identifier: z.string().min(1),
	matchMax: z.number().int().min(0), // 0 = unlimited uses
	contentHtml: z.string()
})
export type AssociableChoice = z.infer<typeof AssociableChoiceSchema>

// MatchInteraction: <qti-match-interaction response-identifier="RESPONSE" shuffle="true" max-associations="4"> ...
export const MatchInteractionCoreSchema = z.object({
	responseIdentifier: z.string().min(1),
	shuffle: z.boolean(),
	maxAssociations: z.number().int().min(0), // 0 = unlimited total associations
	sourceChoices: z.array(AssociableChoiceSchema).min(1), // First <qti-simple-match-set>
	targetChoices: z.array(AssociableChoiceSchema).min(1), // Second <qti-simple-match-set>
	promptHtml: z.string()
})
export const MatchInteractionSchema = MatchInteractionCoreSchema.extend({
	type: z.literal("matchInteraction")
})
export type MatchInteraction = z.infer<typeof MatchInteractionSchema>

export const AnyInteractionSchema = z.discriminatedUnion("type", [
	ChoiceInteractionSchema,
	InlineChoiceInteractionSchema,
	TextEntryInteractionSchema,
	OrderInteractionSchema,
	GapMatchInteractionSchema,
	MatchInteractionSchema
])
export type AnyInteraction = z.infer<typeof AnyInteractionSchema>

// CorrectResponse inside ResponseDeclaration
export const CorrectResponseSchema = z.object({
	values: z.array(z.string().min(1)).min(1)
})
export type CorrectResponse = z.infer<typeof CorrectResponseSchema>

// ResponseDeclaration
export const ResponseDeclarationSchema = z.object({
	identifier: z.string().min(1),
	cardinality: QtiCardinalitySchema,
	baseType: QtiBaseTypeSchema,
	correctResponse: CorrectResponseSchema,
	// Optional response mapping for map-response processing (used for summed feedback/score)
	mapping: z
		.object({
			defaultValue: z.number().default(0),
			lowerBound: z.number().optional(),
			upperBound: z.number().optional(),
			entries: z.array(
				z.object({
					key: z.string().min(1),
					value: z.number()
				})
			)
		})
		.optional()
})
export type ResponseDeclaration = z.infer<typeof ResponseDeclarationSchema>

// OutcomeDeclaration
export const OutcomeDefaultValueSchema = z.object({
	value: z.string()
})
export type OutcomeDefaultValue = z.infer<typeof OutcomeDefaultValueSchema>

export const OutcomeDeclarationSchema = z.object({
	identifier: z.string().min(1),
	cardinality: QtiCardinalitySchema,
	baseType: QtiBaseTypeSchema,
	defaultValue: OutcomeDefaultValueSchema.optional()
})
export type OutcomeDeclaration = z.infer<typeof OutcomeDeclarationSchema>

// Feedback blocks
export const FeedbackBlockSchema = z.object({
	outcomeIdentifier: z.string().min(1),
	identifier: z.string().min(1),
	showHide: z.enum(["show", "hide"]).default("show"),
	contentHtml: z.string()
})
export type FeedbackBlock = z.infer<typeof FeedbackBlockSchema>

// ItemBody as typed content blocks + feedback blocks
const StimulusBlockSchema = z.object({
	type: z.literal("stimulus"),
	html: z.string()
})

// RichStimulus: HTML with inline embeds map keyed by responseIdentifier
export const RichStimulusBlockSchema = z.object({
	type: z.literal("richStimulus"),
	html: z.string(),
	inlineEmbeds: z.record(z.string(), InlineChoiceInteractionSchema),
	textEmbeds: z.record(z.string(), TextEntryInteractionSchema)
})
export type RichStimulusBlock = z.infer<typeof RichStimulusBlockSchema>

const InteractionBlockSchema = z.object({
	type: z.literal("interaction"),
	interaction: AnyInteractionSchema
})

export const ItemBodySchema = z.object({
	contentBlocks: z.array(z.union([StimulusBlockSchema, RichStimulusBlockSchema, InteractionBlockSchema])).min(1),
	feedbackBlocks: z.array(FeedbackBlockSchema)
})
export type ItemBody = z.infer<typeof ItemBodySchema>

// Response processing: Define schemas for the Response Processing AST
const BaseRuleSchema = z.object({
	type: z.literal("setOutcomeValue"),
	identifier: z.string(),
	value: z.string()
})
export type BaseRule = z.infer<typeof BaseRuleSchema>

const MatchConditionSchema = z.object({
	type: z.literal("match"),
	variable: z.string(),
	correct: z.literal(true) // Match against correct response
})
export type MatchCondition = z.infer<typeof MatchConditionSchema>

// NEW: Schema for matching against a specific value (e.g., <qti-match> with <qti-base-value>)
const MatchValueConditionSchema = z.object({
	type: z.literal("matchValue"),
	variable: z.string(), // The response identifier from <qti-variable>
	value: z.string() // The target value from <qti-base-value>
})
export type MatchValueCondition = z.infer<typeof MatchValueConditionSchema>

// Schema for string matching (text entry with case sensitivity)
const StringMatchConditionSchema = z.object({
	type: z.literal("stringMatch"),
	variable: z.string(),
	value: z.string(),
	caseSensitive: z.boolean()
})
export type StringMatchCondition = z.infer<typeof StringMatchConditionSchema>

// Schema for member checking (multi-select subset logic)
const MemberConditionSchema = z.object({
	type: z.literal("member"),
	variable: z.string(),
	value: z.string()
})
export type MemberCondition = z.infer<typeof MemberConditionSchema>

// Schema for the map-response-equal condition
const EqualMapResponseConditionSchema = z.object({
	type: z.literal("equalMapResponse"),
	variable: z.string(), // The response identifier from <qti-map-response>
	value: z.string() // The target sum value from <qti-base-value>
})
export type EqualMapResponseCondition = z.infer<typeof EqualMapResponseConditionSchema>

// Schema for numeric qti-equal / qti-equal-rounded against a base-value literal
const NumericEqualConditionSchema = z.object({
	type: z.literal("numericEqual"),
	variable: z.string(),
	value: z.string(),
	baseType: z.enum(["integer", "float"]),
	rounding: z
		.object({
			mode: z.enum(["significantFigures", "decimalPlaces"]),
			figures: z.number().int().positive()
		})
		.optional()
})
export type NumericEqualCondition = z.infer<typeof NumericEqualConditionSchema>

// Define types first for recursive schemas
export type AndCondition = {
	type: "and"
	conditions: AnyCondition[]
}

export type OrCondition = {
	type: "or"
	conditions: AnyCondition[]
}

export type NotCondition = {
	type: "not"
	condition: AnyCondition
}

export type AnyCondition =
	| MatchCondition
	| MatchValueCondition
	| StringMatchCondition
	| MemberCondition
	| AndCondition
	| OrCondition
	| NotCondition
	| EqualMapResponseCondition
	| NumericEqualCondition

// Then define schemas using the types
const AndConditionSchema: z.ZodType<AndCondition> = z.object({
	type: z.literal("and"),
	conditions: z.array(z.lazy((): z.ZodType<AnyCondition> => AnyConditionSchema))
})

const OrConditionSchema: z.ZodType<OrCondition> = z.object({
	type: z.literal("or"),
	conditions: z.array(z.lazy((): z.ZodType<AnyCondition> => AnyConditionSchema))
})

const NotConditionSchema: z.ZodType<NotCondition> = z.object({
	type: z.literal("not"),
	condition: z.lazy((): z.ZodType<AnyCondition> => AnyConditionSchema)
})

// Union of all condition types
const AnyConditionSchema: z.ZodType<AnyCondition> = z.union([
	MatchConditionSchema,
	MatchValueConditionSchema,
	StringMatchConditionSchema,
	MemberConditionSchema,
	z.lazy((): z.ZodType<AndCondition> => AndConditionSchema),
	z.lazy((): z.ZodType<OrCondition> => OrConditionSchema),
	z.lazy((): z.ZodType<NotCondition> => NotConditionSchema),
	EqualMapResponseConditionSchema,
	NumericEqualConditionSchema
])

// --- NEW STRUCTURE FOR PROCESSING RULES ---

// A single branch (if, else-if, else)
export type ConditionBranch = {
	condition?: AnyCondition // undefined for 'else'
	actions: BaseRule[]
	nestedRules?: ResponseRule[] // Recursive: nested rules inside a branch
}

// Zod schema for a branch
export const ConditionBranchSchema: z.ZodType<ConditionBranch> = z.object({
	condition: AnyConditionSchema.optional(),
	actions: z.array(BaseRuleSchema),
	nestedRules: z.array(z.lazy((): z.ZodType<ResponseRule> => ResponseRuleSchema)).optional()
})

// The main ResponseRule type (either a condition block or a direct action)
export type ResponseRule = { type: "condition"; branches: ConditionBranch[] } | { type: "action"; action: BaseRule }

// Zod schema for ResponseRule
export const ResponseRuleSchema: z.ZodType<ResponseRule> = z.union([
	z.object({
		type: z.literal("condition"),
		branches: z.array(ConditionBranchSchema)
	}),
	z.object({
		type: z.literal("action"),
		action: BaseRuleSchema
	})
])

// Response processing: scoring
export const ScoringRuleSchema = z.object({
	responseIdentifier: z.string().min(1),
	correctScore: z.number(),
	incorrectScore: z.number()
})
export type ScoringRule = z.infer<typeof ScoringRuleSchema>

// UPDATED: The primary ResponseProcessingSchema now uses the AST directly
export const ResponseProcessingSchema = z.object({
	rules: z.array(ResponseRuleSchema),
	scoring: ScoringRuleSchema
})
export type ResponseProcessing = z.infer<typeof ResponseProcessingSchema>

// Root assessment item, extensible for any interaction type
export const AssessmentItemSchema = z.object({
	identifier: z.string().min(1),
	title: z.string().min(1),
	timeDependent: z.boolean(),
	xmlLang: z.string().min(1),

	responseDeclarations: z.array(ResponseDeclarationSchema).min(1),
	outcomeDeclarations: z.array(OutcomeDeclarationSchema).min(1),

	itemBody: ItemBodySchema,
	responseProcessing: ResponseProcessingSchema
})
export type AssessmentItem = z.infer<typeof AssessmentItemSchema>

// ============================================================================
// Assessment Stimulus (reading material / articles)
// ============================================================================

/**
 * Represents a QTI 3.0 Assessment Stimulus.
 * Stimuli are standalone reading materials that can be referenced by assessment items.
 */
export const AssessmentStimulusSchema = z.object({
	/** Unique identifier for the stimulus */
	identifier: z.string().min(1),
	/** Human-readable title */
	title: z.string().default(""),
	/** Language code (e.g., "en", "es") */
	xmlLang: z.string().default("en"),
	/** The HTML content of the stimulus body */
	bodyHtml: z.string()
})
export type AssessmentStimulus = z.infer<typeof AssessmentStimulusSchema>
